import { useLocation } from 'react-router-dom';
import { Lightbulb } from 'lucide-react';
import { useMobileLayout } from '@/mobile/layout';
import { readTipsFlag, tipsAllowed, tipsFor } from './registry';
import { useMyPrefs, useUpdatePrefs } from './prefs';

/**
 * t297u: the first-visit quick tips card. Mounted once (App.tsx); picks the current screen's tips from the registry
 * and shows them until Got it, which is remembered for this person on every device.
 */
export function ScreenTips() {
  const { pathname, search } = useLocation();
  const mobile = useMobileLayout();
  const prefs = useMyPrefs();
  const update = useUpdatePrefs();
  const set = tipsFor(pathname, mobile ? 'mobile' : 'desktop');
  const allowed = tipsAllowed(readTipsFlag(search), typeof navigator !== 'undefined' && Boolean(navigator.webdriver));
  if (!set || !allowed || !prefs.data || prefs.data.tips_off || prefs.data.prefs.seen_tips?.[set.id]) return null;
  const done = () => update.mutate({ seen_tips: { [set.id]: new Date().toISOString() } });
  return (
    <section role="dialog" aria-label={`${set.title} tips`} data-testid="screen-tips" data-tips={set.id}
      className={`fixed z-40 rounded-lg bg-white p-4 shadow-lg ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700 ${mobile ? 'inset-x-3 top-[calc(env(safe-area-inset-top)+4rem)]' : 'right-6 top-36 w-80'}`}>
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <Lightbulb className="h-4 w-4 text-slate-500" aria-hidden />Quick tips
      </h2>
      <ul className="mt-2 space-y-1.5 text-sm text-slate-700 dark:text-slate-300">
        {set.tips.map(t => <li key={t} className="flex gap-2"><span aria-hidden className="text-slate-400">•</span><span>{t}</span></li>)}
      </ul>
      <div className="mt-3 flex justify-end">
        <button type="button" onClick={done} data-testid="screen-tips-done"
          className={`rounded-md bg-accent font-semibold text-on-accent ${mobile ? 'px-4 py-2.5 text-base' : 'px-3 py-1.5 text-sm'}`}>Got it</button>
      </div>
    </section>
  );
}

/** "Show tips again": clears every seen tip. Saved only when something was actually reset. */
export function useResetTips() {
  const prefs = useMyPrefs();
  const update = useUpdatePrefs();
  const seen = Object.keys(prefs.data?.prefs.seen_tips ?? {}).length;
  return {
    seen,
    ready: Boolean(prefs.data),
    pending: update.isPending,
    reset: async () => { if (!seen) return false; await update.mutateAsync({ seen_tips: null }); return true; },
  };
}
